import React, { useEffect, useState, useCallback } from "react";import {
  Box,
  Typography,
  Stack,
  IconButton,
  Divider,
  Card,
  CardContent,
  Avatar,
  Grid,
  Skeleton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import SecurityIcon from "@mui/icons-material/Security";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";
import config from "../../Config";

export default function AdminDetail() {
  const { id } = useParams();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAdminDetail = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const res = await fetch(`${config.apiBaseUrl}/admin/get/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (res.ok) {
        setAdmin(data.admin);
      } else {
        throw new Error(data.message || "Failed to load admin details");
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAdminDetail();
  }, [fetchAdminDetail]);

  return (
    <Box sx={{ marginTop: "10px" }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <IconButton onClick={() => window.history.back()} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700}>
          Admin Profile
        </Typography>
      </Stack>

      <Card elevation={4} sx={{ p: 2, mb: 3 }}>
        <CardContent>
          {loading ? (
            <>
              <Skeleton variant="circular" width={80} height={80} sx={{ mb: 2 }} />
              <Skeleton variant="text" width={250} height={40} />
              <Skeleton variant="text" width={180} height={30} sx={{ mb: 3 }} />

              <Skeleton variant="rectangular" height={200} />
            </>
          ) : !admin ? (
            <Typography color="error" variant="h6">
              Failed to load admin details.
            </Typography>
          ) : (
            <>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={3}
                alignItems={{ xs: "flex-start", sm: "center" }}
                mb={3}
              >
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: "primary.main",
                    fontSize: "2rem",
                  }}
                >
                  {admin.name?.charAt(0)}
                </Avatar>

                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {admin.name}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Admin ID: {admin._id}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <DetailRow
                    icon={<EmailIcon color="primary" />}
                    label="Email"
                    value={admin.email}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <DetailRow
                    icon={<SecurityIcon color="primary" />}
                    label="Access Level"
                    value={admin.accessLevel}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <DetailRow
                    icon={<CalendarMonthIcon color="primary" />}
                    label="Created At"
                    value={new Date(admin.createdAt).toLocaleString()}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

function DetailRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      {icon}
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6" fontWeight={600}>
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}